/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: AI Video Generator 首屏视频播放器 - 支持角色切换、自动播放、缓存
 */
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CHARACTER_VIDEOS, CharacterVideo } from './characterData';
import { useVideoGeneratorPlayer } from './useVideoGeneratorPlayer';

interface VideoGeneratorHeroPlayerProps {
  characters?: CharacterVideo[];
  autoPlay?: boolean;
  loop?: boolean;
  defaultIndex?: number;
  className?: string;
}

const VideoGeneratorHeroPlayer: React.FC<VideoGeneratorHeroPlayerProps> = ({
  characters = CHARACTER_VIDEOS,
  autoPlay = true,
  loop = true,
  defaultIndex = 2,
  className = '',
}) => {
  const {
    activeVideoRef,
    bufferVideoRef,
    currentCharacter,
    currentIndex,
    isMuted,
    isReady,
    toggleMute,
    changeCharacter,
    previousCharacter,
    nextCharacter,
  } = useVideoGeneratorPlayer({
    characters,
    autoPlay,
    loop,
    defaultIndex,
  });

  // 角色选择器滚动
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showNavigation, setShowNavigation] = useState(false);

  // 检查是否需要显示导航按钮
  const checkScrollable = useCallback(() => {
    if (carouselRef.current) {
      const { scrollWidth, clientWidth } = carouselRef.current;
      setShowNavigation(scrollWidth > clientWidth);
    }
  }, []);

  // 监听容器尺寸变化
  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);

    const resizeObserver = new ResizeObserver(checkScrollable);
    if (carouselRef.current) {
      resizeObserver.observe(carouselRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkScrollable);
      resizeObserver.disconnect();
    };
  }, [checkScrollable]);

  // 滚动到选中的角色
  useEffect(() => {
    if (carouselRef.current) {
      const selectedElement = carouselRef.current.children[currentIndex] as HTMLElement;
      if (selectedElement) {
        const containerRect = carouselRef.current.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();
        const scrollLeft =
          selectedElement.offsetLeft - containerRect.width / 2 + elementRect.width / 2;
        carouselRef.current.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth',
        });
      }
    }
  }, [currentIndex]);

  // 左右滚动控制
  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 120;
      const newScrollLeft =
        carouselRef.current.scrollLeft +
        (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  if (!characters.length) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-4 h-full ${className}`}>
      {/* 视频播放区域 */}
      <div
        className="relative w-full flex-1 min-h-0 overflow-hidden rounded-2xl"
        style={{ aspectRatio: '3/4' }}
      >
        {/* 封面图 - 视频未准备好时显示 */}
        {currentCharacter?.posterUrl && !isReady && (
          <div className="absolute inset-0 z-[1]">
            <Image
              src={currentCharacter.posterUrl}
              alt={currentCharacter.name}
              fill
              className="object-cover"
              priority
            />
          </div>
        )}

        {/* 当前播放的视频元素 */}
        <video
          ref={activeVideoRef}
          src={currentCharacter?.videoUrl}
          poster={currentCharacter?.posterUrl}
          className="absolute inset-0 w-full h-full object-cover"
          playsInline
          muted={isMuted}
          loop={loop}
          preload="auto"
        />

        {/* 缓冲视频元素（隐藏，用于预加载下一个视频） */}
        <video
          ref={bufferVideoRef}
          className="absolute inset-0 w-full h-full object-cover opacity-0 pointer-events-none"
          playsInline
          muted={isMuted}
        />

        {/* 控制按钮 - 右上角 */}
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          {/* 上一个按钮 */}
          <button
            type="button"
            onClick={previousCharacter}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 flex items-center justify-center transition-all duration-300 shadow-lg sm:w-9 sm:h-9"
            aria-label="Previous"
          >
            <LeftOutlined className="text-white text-sm" />
          </button>

          {/* 下一个按钮 */}
          <button
            type="button"
            onClick={nextCharacter}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 flex items-center justify-center transition-all duration-300 shadow-lg sm:w-9 sm:h-9"
            aria-label="Next"
          >
            <RightOutlined className="text-white text-sm" />
          </button>

          {/* 静音切换按钮 */}
          <button
            type="button"
            onClick={toggleMute}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 flex items-center justify-center transition-all duration-300 shadow-lg sm:w-9 sm:h-9"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg
                className="w-5 h-5 text-white sm:w-4 sm:h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-white sm:w-4 sm:h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* 脚本展示区域 */}
      <div className="bg-[#F5F5F5] rounded-lg px-4 py-3 sm:px-3 sm:py-2">
        <p className="text-sm font-semibold text-gray-900 mb-1">Script:</p>
        <p className="text-sm text-gray-600 leading-relaxed sm:text-xs">
          {currentCharacter?.script}
        </p>
      </div>

      {/* 角色选择器 */}
      <div className="flex items-center justify-center gap-2">
        {/* 左箭头 */}
        {showNavigation && (
          <button
            type="button"
            onClick={() => scrollCarousel('left')}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Scroll left"
          >
            <LeftOutlined />
          </button>
        )}

        {/* 角色缩略图滚动容器 */}
        <div
          ref={carouselRef}
          className="flex gap-3 overflow-x-auto py-2 scroll-smooth sm:gap-2 justify-center"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {characters.map((character, index) => (
            <button
              key={character.id}
              type="button"
              onClick={() => changeCharacter(index)}
              className={`flex-shrink-0 w-[100px] h-[100px] rounded-xl overflow-hidden border-2 transition-all duration-200 hover:scale-105 sm:w-[80px] sm:h-[80px] ${
                index === currentIndex
                  ? 'border-[#6366F1] shadow-lg shadow-[#6366F1]/30'
                  : 'border-transparent hover:border-gray-300'
              }`}
              aria-label={`Select ${character.name}`}
            >
              <div className="relative w-full h-full">
                <Image
                  src={character.thumbnailUrl}
                  alt={character.name}
                  fill
                  className="object-cover"
                  sizes="100px"
                />
              </div>
            </button>
          ))}
        </div>

        {/* 右箭头 */}
        {showNavigation && (
          <button
            type="button"
            onClick={() => scrollCarousel('right')}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Scroll right"
          >
            <RightOutlined />
          </button>
        )}
      </div>

      {/* 隐藏滚动条样式 */}
      <style jsx>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default VideoGeneratorHeroPlayer;
